import React, { useState, useEffect } from 'react';
import apiConfig from '../../config/apiConfig';
import './ManageTickerFeed.css';

const ManageTickerFeed = () => {
  const [tickerText, setTickerText] = useState('');
  const [originalText, setOriginalText] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('');
  const [error, setError] = useState(null);

  // Fetch current ticker feed text when component mounts
  useEffect(() => {
    const fetchTickerData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(apiConfig.tickerFeed, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data && data.ticker) {
            setTickerText(data.ticker);
            setOriginalText(data.ticker);
          } else {
            setTickerText('Welcome to Legacy Reborn! A reimagining of a classic game world.');
            setOriginalText('Welcome to Legacy Reborn! A reimagining of a classic game world.');
          }
        } else {
          throw new Error(`Failed to fetch ticker data: ${response.status}`);
        }
      } catch (err) {
        console.error('Error fetching ticker data:', err);
        setError('Failed to load the current ticker feed. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTickerData();
  }, []);

  // Handle form submission to update ticker feed
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSaveStatus('');
    setError(null);

    try {
      const response = await fetch(apiConfig.tickerFeed, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ticker: tickerText })
      });

      if (response.ok) {
        setSaveStatus('Ticker feed updated successfully!');
        setOriginalText(tickerText);
      } else {
        throw new Error(`Failed to update ticker: ${response.status}`);
      }
    } catch (err) {
      console.error('Error updating ticker feed:', err);
      setError('Failed to update the ticker feed. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle text change in the editor
  const handleTextChange = (e) => {
    setTickerText(e.target.value);
  };

  // Reset to the last saved version
  const handleReset = () => {
    setTickerText(originalText);
    setSaveStatus('');
    setError(null);
  };

  // Preview how the ticker will look
  const previewText = () => {
    // Format similar to how it appears in the ticker
    const parts = tickerText.split('•');
    const formattedText = parts.join(' ★ ');
    return formattedText;
  };

  return (
    <div className="manage-ticker-feed">
      <h2>Manage Ticker Feed</h2>
      
      {error && <div className="error-message">{error}</div>}
      {saveStatus && <div className="success-message">{saveStatus}</div>}
      
      {isLoading ? (
        <div className="loading">Loading ticker feed data...</div>
      ) : (
        <>
          <div className="instructions">
            <p>Enter the ticker feed text below. Use the "•" symbol to separate different announcements.</p>
            <p>Example: "Welcome to Legacy Reborn! • New event starting soon! • Check the forums for updates."</p>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="tickerText">Ticker Feed Text:</label>
              <textarea
                id="tickerText"
                value={tickerText}
                onChange={handleTextChange}
                rows="6"
                className="ticker-editor"
                placeholder="Enter ticker feed text here..."
              />
            </div>
            
            <div className="preview-section">
              <h3>Preview:</h3>
              <div className="ticker-preview">
                <div className="preview-content">{previewText()}</div>
              </div>
            </div>
            
            <div className="button-group">
              <button type="submit" className="save-button" disabled={isLoading}>
                {isLoading ? 'Saving...' : 'Update Ticker Feed'}
              </button>
              <button type="button" className="reset-button" onClick={handleReset} disabled={isLoading || tickerText === originalText}>
                Reset
              </button>
            </div>
          </form>
          
          <div className="ticker-help">
            <h3>Tips:</h3>
            <ul>
              <li>Use short, concise announcements for better readability</li>
              <li>The ticker will automatically format each section separated by "•"</li>
              <li>Updates may take a few minutes to appear across all user sessions</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default ManageTickerFeed;
